import { ReactNode } from 'react';
import { Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Toolbar,
  QuickFilter,
  ToolbarButton,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
} from '@mui/x-data-grid';

import { OwnerState } from './types';

const StyledQuickFilter = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center',
});

const StyledToolbarButton = styled(ToolbarButton)<{ ownerState: OwnerState }>(
  ({ theme, ownerState }) => ({
    zIndex: 1,
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    opacity: ownerState.expanded ? 0 : 1,
    transition: theme.transitions.create(['opacity']),
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
  })
);

const StyledTextField = styled(TextField)<{
  ownerState: OwnerState;
}>(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  opacity: ownerState.expanded ? 1 : 0,
  width: ownerState.expanded ? 260 : 'var(--trigger-width)',
  transition: theme.transitions.create(['width', 'opacity']),
}));

interface CrudToolbarProps {
  className?: string;
  children?: ReactNode;
  showQuickFilter?: boolean;
}

export const CrudToolbar = ({
  children,
  className,
  showQuickFilter = true,
}: CrudToolbarProps) => {
  return (
    <Toolbar className={className}>
      <Box
        sx={{
          gap: 2,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: showQuickFilter
            ? children
              ? 'space-between'
              : 'flex-start'
            : 'flex-end',
        }}
      >
        {showQuickFilter && (
          <StyledQuickFilter>
            <QuickFilterTrigger
              render={(triggerProps, state) => (
                <Tooltip title="Search" enterDelay={0}>
                  <StyledToolbarButton
                    {...triggerProps}
                    color="default"
                    aria-disabled={state.expanded}
                    ownerState={{ expanded: state.expanded }}
                  >
                    <SearchIcon fontSize="small" />
                  </StyledToolbarButton>
                </Tooltip>
              )}
            />
            <QuickFilterControl
              render={({ ref, ...controlProps }, state) => (
                <StyledTextField
                  {...controlProps}
                  size="small"
                  inputRef={ref}
                  aria-label="Search"
                  placeholder="Search..."
                  ownerState={{ expanded: state.expanded }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: state.value ? (
                        <InputAdornment position="end">
                          <QuickFilterClear
                            edge="end"
                            size="small"
                            aria-label="Clear search"
                            material={{ sx: { marginRight: -0.75 } }}
                          >
                            <CancelIcon fontSize="small" />
                          </QuickFilterClear>
                        </InputAdornment>
                      ) : null,
                      ...controlProps.slotProps?.input,
                    },
                    ...controlProps.slotProps,
                  }}
                />
              )}
            />
          </StyledQuickFilter>
        )}
        {children ? (
          <Box
            sx={{
              gap: 1,
              display: 'flex',
              alignItems: 'center',
              ml: showQuickFilter ? 0 : 'auto',
            }}
          >
            {children}
          </Box>
        ) : null}
      </Box>
    </Toolbar>
  );
};
